import { fn } from '@ember/helper';
import { trackedObject } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismInput, PrismModal } from '@mylighthouse/prism-ember';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import MarketCustomReportingFormFieldsBreadcrumbs from 'lumen/components/market/custom-reporting/form/fields/breadcrumbs';
import type { LookerFolder } from 'lumen/models';

type Changeset = Pick<LookerFolder, 'name'>;

interface MarketCustomReportingFormFolderSignature {
  Args: {
    close: () => void;
    folder: LookerFolder;
  };
}

export default class MarketCustomReportingFormFolder extends Component<MarketCustomReportingFormFolderSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];

  changeset = trackedObject<Changeset>({
    name: this.args.folder.name,
  });

  saveTask = task({ drop: true }, async (): Promise<void> => {
    const { close, folder } = this.args;
    const { name } = this.changeset;

    try {
      folder.name = name;

      await folder.save();
    } catch (e) {
      folder.rollbackAttributes();

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
      this.errorTracking.captureException(e);
    }

    close();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNameConflict() {
    const { name } = this.changeset;
    const { folder } = this.args;
    const parentFolder = folder.folder;

    if (name === folder.name) {
      return false;
    }

    const folders = parentFolder.folders.map((f) =>
      f.name?.toLocaleLowerCase(),
    );
    return folders.includes(name.toLocaleLowerCase());
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSaveDisabled() {
    const { name } = this.changeset;

    return !name || this.hasNameConflict || name === this.args.folder.name;
  }

  <template>
    <PrismModal
      @onClose={{@close}}
      @title={{if @folder.isNew (t "custom_reporting.new_folder") @folder.name}}
      class="custom-reporting__modal"
      data-test-id="folder-modal"
    >
      <:default>
        <div class="modal--padded custom-reporting-form">
          <MarketCustomReportingFormFieldsBreadcrumbs
            @folder={{@folder.folder}}
          />
          <div class="flex w-full flex-row">
            <PrismInput
              @autofocus={{true}}
              @errorMessage={{t "custom_reporting.form.folder_name_conflict"}}
              @hasErrors={{this.hasNameConflict}}
              @isTouched={{true}}
              @label={{t "custom_reporting.form.title"}}
              @onInput={{pick "target.value" (fn (mut this.changeset.name))}}
              @placeholder={{t "custom_reporting.new_folder"}}
              @required={{true}}
              @value={{this.changeset.name}}
              data-test-id="folder-name-input"
            />
          </div>
        </div>
      </:default>
      <:footer>
        <div class="flex h-full items-center justify-end gap-300 pr-500 pl-500">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@close}}
            data-test-id="cancel-button"
          />

          <PrismButton
            @isDisabled={{or this.isSaveDisabled this.saveTask.isRunning}}
            @isLoading={{this.saveTask.isRunning}}
            @label={{if @folder.isNew (t "general.create") (t "general.save")}}
            @onClick={{this.saveTask.perform}}
            data-test-id="save-button"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
